import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import type {
  ApiClient, Heizungtyp, Heizungbauteilart, Heizungproduktreihe,
} from '../api';
import { IconOk } from '../Icons';
import { inDieAblage } from '../kopieren';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Datenpflege für Heizung und Kälte.
 *
 * --- Warum eine eigene Seite ---------------------------------------------------
 *
 * Die Baumansicht dient dem Nachschlagen: Was steckt in diesem Raum? Die Pflege
 * arbeitet quer dazu — welche Typen haben noch kein Datenblatt, wo fehlt die
 * Herstellerseite. Beides in eine Seite zu zwingen hieße, für jede Aufgabe die
 * falsche Gliederung zu haben.
 *
 * --- Warum nach Typ und nicht nach Bauteil -------------------------------------
 *
 * Ein Datenblatt gilt für eine Familie und einen Typ. 304 Ventilatorkonvektoren
 * desselben Typs teilen sich eines — 304-mal dasselbe zuzuweisen wäre Arbeit ohne
 * Ertrag und würde bei der 305. Kopie vergessen.
 *
 * Diese Seite ist die Vorlage für die übrigen Gewerke: Sanitär, Sprinkler,
 * Elektro, MSR. Der Aufbau bleibt, nur der Feldkatalog wechselt.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

export function HeizungDatenpflege() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [typen, setTypen] = useState<Heizungtyp[] | null>(null);
  const [arten, setArten] = useState<Heizungbauteilart[]>([]);
  const [reihen, setReihen] = useState<Heizungproduktreihe[]>([]);
  const [artfilter, setArtfilter] = useState('');
  const [zustandfilter, setZustandfilter] = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    if (!projekt) return;

    try {
      const [t, a, r] = await Promise.all([
        api.heizungTypen(projekt.id, {
          bauteilart: artfilter || undefined,
          nurOhneDatenblatt: nurOffen || undefined,
        }),
        api.heizungBauteilarten(),
        api.heizungProduktreihen(projekt.id),
      ]);

      setTypen(t);
      setArten(a.filter((x) => x.wirdGefuehrt));
      setReihen(r);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [api, projekt?.id, artfilter, nurOffen]);

  const gefiltert = useMemo(() => {
    if (!typen) return null;

    let liste = typen;

    if (zustandfilter === 'offen') liste = liste.filter((x) => x.offen > 0);
    if (zustandfilter === 'pruefen') liste = liste.filter((x) => x.zuPruefen > 0);
    if (zustandfilter === 'fertig')
      liste = liste.filter((x) => x.offen === 0 && x.zuPruefen === 0);

    if (!suche.trim()) return liste;

    const t = suche.trim().toLowerCase();

    return liste.filter((x) =>
      (x.typ ?? '').toLowerCase().includes(t)
      || (x.familie ?? '').toLowerCase().includes(t)
      || (x.hersteller ?? '').toLowerCase().includes(t)
      || (x.artikelnummer ?? '').toLowerCase().includes(t));
  }, [typen, suche, zustandfilter]);

  // Wie weit ist die Pflege? Nur Typen zählen, die ein Datenblatt brauchen —
  // eine Absperrarmatur braucht keines, und sie in die Quote zu nehmen ließe
  // sie nie voll werden.
  const pflichtig = (typen ?? []).filter((t) => t.brauchtDatenblatt);
  const versorgt = pflichtig.filter((t) => t.datasheetId);

  const spalten: Spalte<Heizungtyp>[] = [
    {
      schluessel: 'art', titel: 'Art', breite: '160px', wert: (t) => t.artbezeichnung,
      inhalt: (t) => <StatusBadge klein zustand="neutral" text={t.artbezeichnung} />,
    },
    {
      schluessel: 'typ', titel: 'Typ', immerSichtbar: true,
      wert: (t) => `${t.typ ?? ''} ${t.familie ?? ''}`.trim(),
      inhalt: (t) => (
        <>
          {t.typ ?? '—'}
          <div className="sehr-schwach" style={{ fontSize: 10 }}>{t.familie}</div>
        </>
      ),
    },
    { schluessel: 'anzahl', titel: 'Stück', art: 'zahl', wert: (t) => t.anzahl },
    {
      schluessel: 'hersteller', titel: 'Hersteller',
      wert: (t) => `${t.hersteller ?? ''} ${t.artikelnummer ?? ''}`.trim(),
      inhalt: (t) => (
        <span className="schwach">
          {t.hersteller ?? '—'}
          {t.artikelnummer && (
            <div className="sehr-schwach" style={{ fontSize: 10 }}>
              {t.artikelnummer}
            </div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'daten', titel: 'Daten', art: 'status', breite: '260px',
      sortierbar: false,
      wert: (t) => (t.zugewiesen === t.anzahl ? 'vollständig'
        : `${t.zugewiesen} zugewiesen, ${t.zuPruefen} prüfen, ${t.offen} offen`),
      inhalt: (t) => (
        <DatenZelle
          t={t} api={api} projektId={projekt!.id} reihen={reihen}
          melde={(m) => { setMeldung(m); laden(); }}
        />
      ),
    },
    {
      schluessel: 'datenblatt', titel: 'Datenblatt', breite: '260px',
      sortierbar: false,
      wert: (t) => (t.verweis ? (t.dateiname ?? 'vorhanden') : ''),
      inhalt: (t) => (
        <DatenblattZelle
          t={t} api={api} projektId={projekt!.id}
          melde={(m) => { setMeldung(m); laden(); }}
        />
      ),
    },
    {
      schluessel: 'herstellerseite', titel: 'Herstellerseite', breite: '220px',
      sortierbar: false, wert: (t) => t.herstellerUrl,
      inhalt: (t) => (
        <HerstellerUrlZelle
          t={t} api={api} projektId={projekt!.id}
          melde={(m) => { setMeldung(m); laden(); }}
        />
      ),
    },
  ];

  const kopf = (
    <PageHeader
      titel="Datenpflege Heizung und Kälte"
      beschreibung={'Je Typ und nicht je Bauteil: Ein Datenblatt gilt für eine '
        + 'Familie und einen Typ. 304 Ventilatorkonvektoren desselben Typs teilen '
        + 'sich eines.'}
      meta={<Link to="../heizung-kaelte">zur Baumansicht</Link>}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Datenpflege konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); laden(); }}
        />
      </>
    );
  }

  if (!projekt || !typen) {
    return (
      <>
        {kopf}
        <LoadingState text="Typen werden geladen …" zeilen={10} spalten={6} />
      </>
    );
  }

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="Typen" wert={typen.length} />
        <KpiCard titel="mit Datenblattpflicht" wert={pflichtig.length} />
        <KpiCard
          titel="davon versorgt" wert={versorgt.length}
          vergleich={pflichtig.length > 0 ? `von ${pflichtig.length}` : undefined}
          zustand={pflichtig.length > 0 && versorgt.length < pflichtig.length
            ? 'warnung' : 'gut'}
          zustandText={pflichtig.length > 0 && versorgt.length < pflichtig.length
            ? `${pflichtig.length - versorgt.length} ohne Datenblatt` : 'vollständig'}
        />
        <KpiCard
          titel="Daten zugewiesen"
          wert={typen.reduce((n, t) => n + t.zugewiesen, 0)}
        />
        <KpiCard
          titel="zu prüfen" wert={typen.reduce((n, t) => n + t.zuPruefen, 0)}
          zustand={typen.some((t) => t.zuPruefen > 0) ? 'warnung' : 'gut'}
          zustandText={typen.some((t) => t.zuPruefen > 0)
            ? 'Kennwert außerhalb der Baugrößen' : 'keiner'}
        />
        <KpiCard titel="offen" wert={typen.reduce((n, t) => n + t.offen, 0)} />
      </KpiReihe>

      {meldung && (
        <div className="hinweis" style={{ display: 'block', marginTop: 12 }}>
          <IconOk size={13} /> {meldung}
        </div>
      )}

      <Katalogbereich
        api={api}
        projektId={projekt.id}
        reihen={reihen}
        neuLaden={laden}
        melde={setMeldung}
      />

      <DataTable
        name="Typen"
        spalten={spalten}
        zeilen={gefiltert ?? []}
        schluessel={(t) => `${t.familie}/${t.typ}`}
        suchbegriff={suche}
        beiSuche={setSuche}
        suchePlatzhalter="Typ, Familie, Hersteller, Artikelnummer"
        ausfuhr
        ausfuhrDateiname="heizung-typen"
        hoehe={640}
        zeilenhoehe={64}
        zeilenKlasse={(t) => (t.brauchtDatenblatt && !t.datasheetId
          ? 'datenfeld' : undefined)}
        werkzeuge={(
          <>
            <select
              value={artfilter}
              onChange={(e) => setArtfilter(e.target.value)}
              aria-label="Bauteilart"
              style={{ width: 'auto' }}
            >
              <option value="">alle Bauteilarten</option>
              {arten.map((a) => (
                <option key={a.schluessel} value={a.schluessel}>{a.bezeichnung}</option>
              ))}
            </select>

            <select
              value={zustandfilter}
              onChange={(e) => setZustandfilter(e.target.value)}
              aria-label="Datenzustand"
              style={{ width: 'auto' }}
            >
              <option value="">jeder Datenzustand</option>
              <option value="offen">enthält Offene</option>
              <option value="pruefen">enthält zu Prüfende</option>
              <option value="fertig">vollständig zugewiesen</option>
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={nurOffen}
                     onChange={(e) => setNurOffen(e.target.checked)} />
              nur ohne Datenblatt
            </label>
          </>
        )}
        leer={(
          <EmptyState
            titel={artfilter || zustandfilter || nurOffen || suche
              ? 'Kein Typ in dieser Auswahl.'
              : 'Für dieses Modell ist noch kein Heizungstyp aufgenommen.'}
            erklaerung={artfilter || zustandfilter || nurOffen || suche
              ? 'Bauteilart, Datenzustand und Suche schränken die Liste ein.'
              : 'Die Typen entstehen aus der Modellaufnahme des Gewerks Heizung/Kälte.'}
            aktion={(artfilter || zustandfilter || nurOffen || suche) ? (
              <button
                className="m-knopf"
                onClick={() => {
                  setArtfilter(''); setZustandfilter('');
                  setNurOffen(false); setSuche('');
                }}
              >
                Filter aufheben
              </button>
            ) : undefined}
          />
        )}
      />
    </>
  );
}

// --- Die Zellen der Typtabelle ----------------------------------------------
//
// Der Bestand hatte EINE Zeilenkomponente `Typzeile`, die alle Bedienelemente
// und ihren Zustand trug. `DataTable` rendert Zellen, keine Zeilen - also
// wandert der Zustand dorthin, wo er hingehört: in die Zelle, die ihn
// braucht. Kein Verhalten geht dabei verloren.

function DatenZelle({
  t, api, projektId, reihen, melde,
}: {
  t: Heizungtyp;
  api: ApiClient;
  projektId: string;
  reihen: Heizungproduktreihe[];
  melde: (meldung: string) => void;
}) {
  const [reiheId, setReiheId] = useState('');
  const [weist, setWeist] = useState(false);

  // Nur Reihen derselben Bauteilart. Ein Heizkörperkatalog an einer Pumpe wäre
  // ein Fehlgriff, den die Liste gar nicht erst ermöglichen soll.
  const passende = reihen.filter(
    (r) => !r.bauteilart || r.bauteilart === t.bauteilart);

  const zuweisen = async (nurPruefen: boolean) => {
    setWeist(true);
    try {
      const a = await api.heizungReiheZuweisen(
        projektId, reiheId, t.familie, t.typ, nurPruefen);

      melde(a ? [a.message, ...(a.hinweise ?? [])].join(' · ')
              : 'Zuweisung durchgeführt.');
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setWeist(false);
    }
  };

  return (
    <div style={{ fontSize: 12 }}>
      <Datenzustand t={t} />

      {passende.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 3, alignItems: 'center' }}>
          <select
            value={reiheId}
            onChange={(e) => setReiheId(e.target.value)}
            aria-label={`Produktreihe für ${t.typ ?? t.familie}`}
            style={{ fontSize: 10, maxWidth: 130 }}
          >
            <option value="">Reihe wählen …</option>
            {passende.map((r) => (
              <option key={r.id} value={r.id}>
                {r.bezeichnung} ({r.baugroessen})
              </option>
            ))}
          </select>

          {reiheId && (
            <>
              <button onClick={() => zuweisen(true)} disabled={weist}
                      className="m-knopf"
                      style={{ padding: '1px 5px', fontSize: 10 }}
                      title="Rechnen, aber nichts ändern">
                prüfen
              </button>
              <button onClick={() => zuweisen(false)} disabled={weist}
                      className="m-knopf m-knopf-haupt"
                      style={{ padding: '1px 5px', fontSize: 10 }}>
                zuweisen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Das PDF hochladen.
 *
 * Ein Zug für die ganze Kette: ablegen, Verweis bilden, allen Bauteilen der
 * Gruppe zuweisen. Jeder Schritt für sich wäre nutzlos — eine abgelegte Datei
 * ohne Verweis findet niemand.
 */
function DatenblattZelle({
  t, api, projektId, melde,
}: {
  t: Heizungtyp;
  api: ApiClient;
  projektId: string;
  melde: (meldung: string) => void;
}) {
  const [laedt, setLaedt] = useState(false);

  const hochladen = async (datei: File) => {
    setLaedt(true);
    try {
      const a = await api.heizungDatenblattHochladen(
        projektId, datei, t.familie, t.typ);

      melde(a.message);
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(false);
    }
  };

  const fehltBlatt = t.brauchtDatenblatt && !t.datasheetId;

  if (t.verweis) {
    return (
      <div style={{ fontSize: 12 }}>
        <a href={t.verweis} target="_blank" rel="noreferrer">
          {t.dateiname ?? 'öffnen'}
        </a>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
          <button
            className="m-knopf"
            onClick={async () => {
              // Über inDieAblage, nicht direkt: navigator.clipboard?.writeText
              // scheitert stillschweigend, wenn die Schnittstelle fehlt.
              const geklappt = await inDieAblage(t.verweis ?? '');

              if (!geklappt) {
                alert('Der Browser erlaubt das Kopieren nicht. Der Verweis:'
                    + '\n\n' + (t.verweis ?? ''));
              }
            }}
            style={{ padding: '1px 6px', fontSize: 10 }}
            title="Der Verweis ist ohne Anmeldung erreichbar"
          >
            Verweis kopieren
          </button>

          <label style={{ fontSize: 10, cursor: 'pointer', textDecoration: 'underline' }}>
            ersetzen
            <input type="file" accept="application/pdf,.pdf" hidden
                   disabled={laedt}
                   onChange={(e) => {
                     const d = e.target.files?.[0];
                     if (d) hochladen(d);
                     e.target.value = '';
                   }} />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
      <label className="m-knopf" style={{ padding: '3px 8px', fontSize: 11 }}>
        {laedt ? 'wird abgelegt …' : 'PDF hochladen'}
        <input type="file" accept="application/pdf,.pdf" hidden
               disabled={laedt}
               onChange={(e) => {
                 const d = e.target.files?.[0];
                 if (d) hochladen(d);
                 e.target.value = '';
               }} />
      </label>

      {fehltBlatt && (
        <StatusBadge
          zustand="warnung" klein text="Datenblatt fehlt"
          titel="Diese Bauteilart braucht ein Datenblatt"
        />
      )}
    </div>
  );
}

function HerstellerUrlZelle({
  t, api, projektId, melde,
}: {
  t: Heizungtyp;
  api: ApiClient;
  projektId: string;
  melde: (meldung: string) => void;
}) {
  const [url, setUrl] = useState(t.herstellerUrl ?? '');
  const [laeuft, setLaeuft] = useState(false);

  const urlSpeichern = async () => {
    if (url === (t.herstellerUrl ?? '')) return;

    setLaeuft(true);
    try {
      // schicke kann void liefern, wenn der Server 204 sendet.
      const a = await api.heizungHerstellerUrl(projektId, t.familie, t.typ, url);

      melde(a?.message ?? `Herstellerseite an ${t.anzahl} Bauteil(en) gesetzt.`);
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <input
      value={url}
      onChange={(e) => setUrl(e.target.value)}
      onBlur={urlSpeichern}
      disabled={laeuft}
      placeholder="https://…"
      aria-label={`Herstellerseite für ${t.typ ?? t.familie}`}
      style={{ width: '100%', minWidth: 160, fontSize: 12 }}
    />
  );
}

// --- Der Datenzustand einer Gruppe -------------------------------------------

/**
 * Wie weit die Gruppe versorgt ist.
 *
 * Drei Zustände, und der mittlere ist der wichtigste:
 *
 *   zugewiesen   Daten liegen an, innerhalb der Baugrößen
 *   prüfen       Daten liegen an, aber der Kennwert lag außerhalb — eine
 *                Hochrechnung über den Katalog hinaus
 *   offen        keine Daten
 *
 * „prüfen" wird hervorgehoben, weil es sonst untergeht. Eine Extrapolation
 * verlässt den Bereich, für den der Hersteller einsteht — wer das nicht sieht,
 * legt darauf eine Anlage aus.
 */
function Datenzustand({ t }: { t: Heizungtyp }) {
  if (t.zugewiesen === t.anzahl) {
    return <StatusBadge zustand="gut" text="vollständig" klein />;
  }

  return (
    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {t.zugewiesen > 0 && (
        <StatusBadge zustand="gut" klein titel="Daten zugewiesen"
                     text={`${t.zugewiesen} zugewiesen`} />
      )}
      {t.zuPruefen > 0 && (
        <StatusBadge zustand="warnung" klein
                     titel="Kennwert außerhalb der Baugrößen — hochgerechnet"
                     text={`${t.zuPruefen} prüfen`} />
      )}
      {t.offen > 0 && (
        <StatusBadge zustand="neutral" klein text={`${t.offen} offen`} />
      )}
    </span>
  );
}

// --- Die Produktkataloge -----------------------------------------------------

/**
 * Eingelesene Herstellerkataloge.
 *
 * Ein Katalog nennt Leistungen für Baugrößen: Baulänge 1000 gibt 1383 W, 1200 gibt
 * 1660 W. Steht im Modell einer mit 1100 mm, wird interpoliert — sofern die Reihe
 * es erlaubt.
 *
 * Bei Heizkörpern ist das zulässig: Leistung über Baulänge ist nahezu linear. Bei
 * Kältemaschinen nicht — dort gibt es Baugrößen, keine Reihe.
 */
function Katalogbereich({
  api, projektId, reihen, neuLaden, melde,
}: {
  api: ApiClient;
  projektId: string;
  reihen: Heizungproduktreihe[];
  neuLaden: () => void;
  melde: (m: string) => void;
}) {
  const [offen] = useState(false);
  const [laedt, setLaedt] = useState(false);

  const einlesen = async (datei: File) => {
    setLaedt(true);
    try {
      const a = await api.heizungKatalogEinlesen(projektId, datei);

      melde([a.message, ...(a.hinweise ?? [])].join(' · '));
      neuLaden();
    } catch (e) {
      melde(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(false);
    }
  };

  const reihenspalten: Spalte<Heizungproduktreihe>[] = [
    {
      schluessel: 'reihe', titel: 'Reihe', immerSichtbar: true,
      wert: (r) => `${r.bezeichnung} ${r.hersteller ?? ''}`.trim(),
      inhalt: (r) => (
        <>
          {r.bezeichnung}
          {r.hersteller && (
            <div className="sehr-schwach" style={{ fontSize: 10 }}>{r.hersteller}</div>
          )}
        </>
      ),
    },
    {
      schluessel: 'art', titel: 'Art', wert: (r) => r.bauteilart ?? 'alle',
      inhalt: (r) => <span className="schwach">{r.bauteilart ?? 'alle'}</span>,
    },
    { schluessel: 'groessen', titel: 'Baugrößen', art: 'zahl', wert: (r) => r.baugroessen },
    {
      schluessel: 'kennfeld', titel: 'Kennfeld', wert: (r) => r.kennfeld,
      inhalt: (r) => (
        <span className="schwach">
          {r.kennfeld ?? '—'}
          {!r.darfInterpolieren && r.baugroessen > 1 && (
            <div className="sehr-schwach" style={{ fontSize: 9 }}
                 title="Nur genau passende Kennwerte werden zugewiesen">
              ohne Interpolation
            </div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'bereich', titel: 'Bereich', wert: (r) => r.von,
      inhalt: (r) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {r.von !== null && r.bis !== null ? `${r.von} – ${r.bis}` : '—'}
          {r.darfExtrapolieren && (
            <div style={{ marginTop: 2 }}>
              <StatusBadge zustand="warnung" klein text="extrapoliert"
                           titel="Werte außerhalb werden hochgerechnet" />
            </div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'auslegung', titel: 'Auslegung', wert: (r) => r.auslegung,
      inhalt: (r) => (
        <span className="schwach" style={{ fontSize: 11 }}>{r.auslegung ?? '—'}</span>
      ),
    },
    { schluessel: 'verwendet', titel: 'verwendet', art: 'zahl', wert: (r) => r.verwendet },
  ];

  return (
    <ContentSection
      titel="Produktkataloge"
      meta={`${reihen.length} Reihe(n)`}
      aufklappbar
      standardOffen={offen}
      beschreibung={'Ein Katalog nennt Leistungen für Baugrößen. Steht im Modell eine '
        + 'Größe dazwischen, wird linear interpoliert — sofern die Reihe es erlaubt. '
        + 'Interpolierte Werte tragen die Herkunft „berechnet“ und sind eine Näherung, '
        + 'keine Herstellerangabe.'}
      aktionen={(
        <label className="m-knopf" style={{ fontSize: 12 }}>
          {laedt ? 'wird eingelesen …' : 'Katalog als JSON einlesen'}
          <input type="file" accept="application/json,.json" hidden
                 disabled={laedt}
                 onChange={(e) => {
                   const d = e.target.files?.[0];
                   if (d) einlesen(d);
                   e.target.value = '';
                 }} />
        </label>
      )}
      nackt
    >
      <DataTable
        name="Produktreihen"
        spalten={reihenspalten}
        zeilen={reihen}
        schluessel={(r) => String(r.id)}
        ausfuhr
        ausfuhrDateiname="heizung-produktreihen"
        leer={(
          <EmptyState
            titel="Für dieses Projekt ist noch kein Produktkatalog eingelesen."
            erklaerung={'Ein Katalog nennt Leistungen je Baugröße und ist die Quelle '
              + 'für die Kennwerte der Heizflächen.'}
          />
        )}
      />
    </ContentSection>
  );
}
